/**
 * Accept Invite Page
 *
 * Public page (no auth required) that shows invitation details
 * and allows the user to accept the invitation via Auth0 login.
 *
 * Handles:
 * - Wrong user logged in → "Switch Account" prompt
 * - User not logged in → Auth0 signup with pre-filled email
 * - Already accepted → success message, not error
 * - Expired/invalid → clear error messaging
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Users, Mail, Shield, AlertCircle, Loader2, CheckCircle, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'https://getclearance-production.up.railway.app';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { loginWithRedirect, logout, isAuthenticated, user, getAccessTokenSilently } = useAuth0();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const acceptAttempted = useRef(false);

  // Fetch invite info on mount
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    async function fetchInviteInfo() {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/invite/${token}`);
        const data = await response.json();

        if (!data.valid) {
          // Handle "already accepted" as a soft success
          if (data.error?.includes('already been used')) {
            setAccepted(true);
            setInviteInfo({ tenant_name: 'your team' });
          } else {
            setError(data.error || 'Invalid invitation');
          }
        } else {
          setInviteInfo(data);
        }
      } catch (err) {
        console.error('Failed to validate invite:', err);
        setError('Failed to validate invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchInviteInfo();
  }, [token]);

  // If user is authenticated AND email matches, try to accept the invite (ONCE)
  useEffect(() => {
    if (!isAuthenticated || !user || !inviteInfo || accepting || accepted) return;
    if (acceptAttempted.current) return;

    // Check email mismatch — don't attempt accept with wrong user
    if (user.email?.toLowerCase() !== inviteInfo.email?.toLowerCase()) return;

    acceptAttempted.current = true;

    async function acceptInvite() {
      setAccepting(true);
      try {
        const accessToken = await getAccessTokenSilently();

        const response = await fetch(`${API_URL}/api/v1/auth/invite/${token}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            auth0_id: user.sub,
            email: user.email,
            name: user.name,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          const detail = errorData.detail || '';

          // Handle "already accepted" as success
          if (detail.includes('already been used')) {
            setAccepted(true);
            return;
          }

          throw new Error(detail || 'Failed to accept invitation');
        }

        setAccepted(true);

        // Redirect to dashboard after short delay
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } catch (err) {
        console.error('Failed to accept invite:', err);
        setError(err.message || 'Failed to accept invitation');
      } finally {
        setAccepting(false);
      }
    }

    acceptInvite();
  }, [isAuthenticated, user, inviteInfo, token, accepting, accepted, getAccessTokenSilently]);

  // Handle accept button click (when not logged in)
  const handleAccept = () => {
    loginWithRedirect({
      appState: { returnTo: `/accept-invite?token=${token}` },
      authorizationParams: {
        login_hint: inviteInfo?.email,
        screen_hint: 'signup',
      },
    });
  };

  // Handle switch account (when logged in as wrong user)
  const handleSwitchAccount = () => {
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/accept-invite?token=${token}`,
      },
    });
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      admin: 'Full access to all features and settings',
      reviewer: 'Can review and approve applicants and cases',
      analyst: 'Can view applicants and manage cases',
      viewer: 'Read-only access to the platform',
    };
    return descriptions[role] || 'Team member';
  };

  // Check if wrong user is logged in
  const isWrongUser = isAuthenticated && user && inviteInfo &&
    user.email?.toLowerCase() !== inviteInfo.email?.toLowerCase();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Loader2 size={32} style={styles.spinner} />
          <p style={styles.loadingText}>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <AlertCircle size={48} />
          </div>
          <h1 style={styles.errorTitle}>Invalid Invitation</h1>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.hintText}>
            Please contact your team administrator for a new invitation.
          </p>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <CheckCircle size={48} />
          </div>
          <h1 style={styles.successTitle}>Welcome to {inviteInfo?.tenant_name || 'your team'}!</h1>
          <p style={styles.successText}>
            Your account has been set up. Redirecting to dashboard...
          </p>
          <button
            style={styles.acceptButton}
            onClick={() => { window.location.href = '/'; }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (accepting) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Loader2 size={32} style={styles.spinner} />
          <p style={styles.loadingText}>Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Wrong user logged in
  if (isWrongUser) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <Shield size={32} />
            <span style={styles.logoText}>TrustGate</span>
          </div>

          <h1 style={styles.title}>You're invited to join</h1>
          <h2 style={styles.tenantName}>{inviteInfo.tenant_name}</h2>

          <div style={styles.mismatchBox}>
            <AlertCircle size={20} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <div>
              <p style={styles.mismatchTitle}>Wrong account</p>
              <p style={styles.mismatchText}>
                You're signed in as <strong>{user.email}</strong>, but this invitation
                is for <strong>{inviteInfo.email}</strong>.
              </p>
            </div>
          </div>

          <button style={styles.acceptButton} onClick={handleSwitchAccount}>
            <LogOut size={18} style={{ marginRight: 8 }} />
            Switch Account
          </button>

          <p style={styles.footer}>
            You'll be signed out and can sign in or create an account as {inviteInfo.email}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logo}>
          <Shield size={32} />
          <span style={styles.logoText}>TrustGate</span>
        </div>

        {/* Heading */}
        <h1 style={styles.title}>You're invited to join</h1>
        <h2 style={styles.tenantName}>{inviteInfo.tenant_name}</h2>

        {/* Invite details */}
        <div style={styles.details}>
          <div style={styles.detailRow}>
            <Mail size={18} style={styles.detailIcon} />
            <div>
              <div style={styles.detailLabel}>Invited as</div>
              <div style={styles.detailValue}>{inviteInfo.email}</div>
            </div>
          </div>

          <div style={styles.detailRow}>
            <Users size={18} style={styles.detailIcon} />
            <div>
              <div style={styles.detailLabel}>Role</div>
              <div style={styles.detailValue}>
                {inviteInfo.role.charAt(0).toUpperCase() + inviteInfo.role.slice(1)}
              </div>
              <div style={styles.roleDescription}>{getRoleDescription(inviteInfo.role)}</div>
            </div>
          </div>
        </div>

        {/* Inviter */}
        <p style={styles.inviterText}>
          Invited by <strong>{inviteInfo.inviter_name}</strong>
        </p>

        {/* Accept button */}
        <button style={styles.acceptButton} onClick={handleAccept}>
          Accept Invitation
        </button>

        {/* Footer */}
        <p style={styles.footer}>
          By accepting, you'll create an account or sign in to join this team.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a0f 0%, #111318 100%)',
    padding: '20px',
  },
  card: {
    background: '#1a1d24',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '440px',
    width: '100%',
    border: '1px solid #2a2f3a',
    textAlign: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: '#0d9488',
    marginBottom: '32px',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f0f2f5',
  },
  title: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#8b919e',
    margin: '0 0 8px',
  },
  tenantName: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#f0f2f5',
    margin: '0 0 32px',
  },
  details: {
    background: '#111318',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '12px 0',
    borderBottom: '1px solid #2a2f3a',
  },
  detailIcon: {
    color: '#0d9488',
    marginTop: '2px',
    flexShrink: 0,
  },
  detailLabel: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailValue: {
    fontSize: '15px',
    color: '#f0f2f5',
    fontWeight: '500',
  },
  roleDescription: {
    fontSize: '13px',
    color: '#8b919e',
    marginTop: '4px',
  },
  inviterText: {
    fontSize: '14px',
    color: '#8b919e',
    marginBottom: '24px',
  },
  acceptButton: {
    width: '100%',
    padding: '14px 24px',
    background: '#0d9488',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '20px',
  },
  spinner: {
    color: '#0d9488',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#8b919e',
    marginTop: '16px',
    fontSize: '15px',
  },
  errorIcon: {
    color: '#ef4444',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f0f2f5',
    margin: '0 0 12px',
  },
  errorText: {
    fontSize: '15px',
    color: '#ef4444',
    marginBottom: '16px',
  },
  hintText: {
    fontSize: '14px',
    color: '#8b919e',
  },
  successIcon: {
    color: '#10b981',
    marginBottom: '16px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f0f2f5',
    margin: '0 0 12px',
  },
  successText: {
    fontSize: '15px',
    color: '#8b919e',
    marginBottom: '24px',
  },
  mismatchBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    background: 'rgba(245, 158, 11, 0.1)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    borderRadius: '10px',
    textAlign: 'left',
    marginBottom: '24px',
  },
  mismatchTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f59e0b',
    margin: '0 0 4px',
  },
  mismatchText: {
    fontSize: '13px',
    color: '#8b919e',
    margin: 0,
    lineHeight: '1.5',
  },
};
